import { useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useClientData } from '../ClientLayout.jsx';
import { useSaveToast } from '../SaveToast.jsx';
import { api } from '../../lib/api.js';
import AddressAutocomplete from '../AddressAutocomplete.jsx';
import {
  decodeDebtorId,
  encodeDebtorId,
  findDebtorRecord,
  debtorRecordKey,
} from '../../lib/debtorRecords.js';
import { upsertDebtorProfile } from '../../lib/debtorProfiles.js';
import FieldTip from '../FieldTip.jsx';
import { remapArchivedDebtorId } from '../../lib/debtorArchive.js';
import {
  debtorDisplayName,
  debtorIdentityErrorMessage,
  normalizeDebtorIdentity,
} from '../../lib/debtorIdentity.js';
import {
  DEBTOR_COUNTRIES,
  coerceDebtorCountry,
  debtorRegionLabel,
  debtorRegionMaxLength,
  debtorRegionPlaceholder,
  debtorPostalPlaceholder,
  normalizeDebtorCountry,
} from '../../lib/debtorCountry.js';
import {
  isValidPostalForCountry,
  normalizePostalCode,
  postalCodeErrorMessage,
} from '../../lib/postalCode.js';
import { isMatterRecordLocked, statusDisplayLabel } from '../../lib/matterPipeline.js';
import { isPaidStatus } from '../../lib/paidBeforeAssignment.js';
import { formatMatterMoney, formatMattersMoneySum } from '../../lib/matterCurrency.js';
import { matterDisplayRef } from '../../lib/demandId.js';
import DebtorKpiStrip from '../DebtorKpiStrip.jsx';
import {
  DEBTOR_HISTORY_EVENT_TYPES,
  debtorHistoryTypeLabel,
  ensureDebtorCreatedHistory,
  getDebtorHistory,
  logDebtorHistory,
  noteHistorySnippet,
  remapDebtorHistory,
  summarizeDebtorChanges,
} from '../../lib/debtorHistory.js';
import {
  findDebtorProfileNotes,
  listDebtorNoteEntries,
} from '../../lib/recordNotes.js';
import {
  fingerprintForeignEntries,
  resolveTabNotifications,
} from '../../lib/recordTabNotifications.js';
import RecordNotesSection from '../RecordNotesSection.jsx';

const DEBTOR_TABS = [
  { id: 'details', label: 'Debtor Details', shortLabel: 'Details' },
  { id: 'demands', label: 'Demands', shortLabel: 'Demands' },
  { id: 'payments', label: 'Payments', shortLabel: 'Payments' },
  { id: 'history', label: 'History', shortLabel: 'History' },
  { id: 'notes', label: 'Notes', shortLabel: 'Notes' },
];

function normalizeTab(raw) {
  const id = String(raw || '').trim().toLowerCase();
  return DEBTOR_TABS.some((tab) => tab.id === id) ? id : 'details';
}

function backPathFrom(location) {
  const from = location.state?.from;
  if (
    typeof from === 'string' &&
    from.startsWith('/client/') &&
    !/^\/client\/debtors\/[^/]+$/.test(from)
  ) {
    return from;
  }
  return '/client/debtors';
}

function formatMoney(n, currencyOrMatter = 'USD') {
  return formatMatterMoney(n, currencyOrMatter);
}

function formatWhen(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return String(value);
  }
}

function statusLabel(status) {
  return statusDisplayLabel(status);
}

function formFromDebtor(debtor) {
  const primary = debtor?.primaryMatter || debtor?.matters?.[0] || {};
  return {
    Debtor_Name: debtor?.name || primary.Debtor_Name || '',
    Debtor_Company: debtor?.company || primary.Debtor_Company || '',
    Debtor_Country: normalizeDebtorCountry(
      debtor?.country || primary.Debtor_Country,
      {
        postalCode: debtor?.postalCode || primary.Debtor_Postal_Code,
        state: debtor?.state || primary.Debtor_State,
      }
    ),
    Debtor_Street: debtor?.street || primary.Debtor_Street || '',
    Debtor_City: debtor?.city || primary.Debtor_City || '',
    Debtor_State: debtor?.state || primary.Debtor_State || '',
    Debtor_Postal_Code: debtor?.postalCode || primary.Debtor_Postal_Code || '',
    Debtor_Email: debtor?.email || primary.Debtor_Email || '',
    External_Account_Number:
      debtor?.accountNumber || primary.External_Account_Number || '',
  };
}

function matterRef(matter) {
  return matterDisplayRef(matter);
}

function MoreActionsIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="5" cy="12" r="1.7" fill="currentColor" />
      <circle cx="12" cy="12" r="1.7" fill="currentColor" />
      <circle cx="19" cy="12" r="1.7" fill="currentColor" />
    </svg>
  );
}

export default function DebtorDetailPage() {
  const { debtorId: debtorIdParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const {
    allDebtors,
    openMatter,
    openIntake,
    setMatters,
    refreshDebtors,
    archiveDebtors,
    unarchiveDebtors,
    setNotice,
  } = useClientData();
  const { flashSave, clearSaveToast } = useSaveToast();
  const debtorId = decodeDebtorId(debtorIdParam);
  const debtor = useMemo(
    () => findDebtorRecord(allDebtors, debtorId),
    [allDebtors, debtorId]
  );

  const [form, setForm] = useState(() => formFromDebtor(debtor));
  const [error, setError] = useState('');
  const [pending, startTransition] = useTransition();
  const [history, setHistory] = useState([]);
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [notesRevision, setNotesRevision] = useState(0);
  const actionsRef = useRef(null);
  const activeTab = normalizeTab(searchParams.get('tab'));

  const backTo = backPathFrom(location);
  const isArchived = Boolean(debtor?.Archived);
  const isProfileOnly = Boolean(debtor?.isProfileOnly) || (debtor?.matters || []).length === 0;
  const editableMatters = useMemo(
    () => (debtor?.matters || []).filter((m) => !isMatterRecordLocked(m.Status)),
    [debtor]
  );
  // Profile-only debtors (no demands yet) stay editable. Matter-backed debtors lock
  // only when every related demand is past send.
  const profileLocked = !isProfileOnly && editableMatters.length === 0;
  const actorName = user?.displayName || user?.email || 'You';

  const paidMatters = useMemo(() => {
    const rows = (debtor?.matters || []).filter((m) => isPaidStatus(m.Status));
    return [...rows].sort((a, b) => {
      const aAt = new Date(a.Paid_At || a.Updated_At || a.Created_At || 0).getTime();
      const bAt = new Date(b.Paid_At || b.Updated_At || b.Created_At || 0).getTime();
      return bAt - aAt;
    });
  }, [debtor]);

  const openBalanceMatters = useMemo(
    () =>
      (debtor?.matters || []).filter(
        (m) => !isPaidStatus(m.Status) && Number(m.Amount_Owed || 0) > 0
      ),
    [debtor]
  );

  const tabSignatures = useMemo(() => {
    const viewer = { email: user?.email, displayName: user?.displayName || user?.name };
    const noteEntries = findDebtorProfileNotes(user?.email, debtor?.id);
    const notes = noteEntries.length ? noteEntries : listDebtorNoteEntries(debtor);
    return {
      // Demands/payments lists lack reliable actor metadata — notify via History/Notes.
      demands: '0',
      payments: '0',
      history: fingerprintForeignEntries(history, viewer),
      notes: fingerprintForeignEntries(notes, viewer),
    };
  }, [debtor, history, notesRevision, user?.email, user?.displayName, user?.name]);

  const tabUnread = useMemo(() => {
    if (!debtor?.id) return {};
    return resolveTabNotifications({
      kind: 'debtor',
      recordId: debtor.id,
      signatures: tabSignatures,
      activeTab,
    }).unread;
  }, [debtor?.id, tabSignatures, activeTab]);

  useEffect(() => {
    setForm(formFromDebtor(debtor));
    setError('');
    clearSaveToast();
    setMetricsOpen(false);
    setActionsOpen(false);
    if (!debtor?.id) {
      setHistory([]);
      return;
    }
    const seeded = ensureDebtorCreatedHistory(debtor, {
      actorName,
      actorRole: 'CLIENT',
    });
    setHistory(seeded);
  }, [debtor?.id]);

  useEffect(() => {
    if (!actionsOpen) return undefined;
    function onPointerDown(e) {
      if (!actionsRef.current?.contains(e.target)) setActionsOpen(false);
    }
    function onKeyDown(e) {
      if (e.key === 'Escape') setActionsOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [actionsOpen]);

  useEffect(() => {
    const el = document.getElementById(`debtor-tab-${activeTab}`);
    el?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [activeTab]);

  function rememberHistory(debtorKey, events) {
    setHistory(events || getDebtorHistory(debtorKey));
  }

  function logHistory(debtorKey, partial) {
    const next = logDebtorHistory(debtorKey, {
      actorName,
      actorRole: 'CLIENT',
      ...partial,
    });
    setHistory(next);
    return next;
  }

  function setTab(nextId) {
    const id = normalizeTab(nextId);
    const next = new URLSearchParams(searchParams);
    if (id === 'details') next.delete('tab');
    else next.set('tab', id);
    setSearchParams(next, { replace: true });
  }

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    clearSaveToast();
  }

  function notifySaved(message) {
    const text = String(message || '').trim();
    if (!text) {
      clearSaveToast();
      return;
    }
    flashSave(text, {
      aboveMobileSave: !profileLocked && activeTab === 'details',
    });
  }

  function handleArchiveToggle() {
    if (!debtor) return;
    if (isArchived) {
      logHistory(debtor.id, {
        type: DEBTOR_HISTORY_EVENT_TYPES.RESTORED,
        summary: 'Debtor restored to the active list',
      });
      unarchiveDebtors?.([debtor]);
      setNotice?.('Debtor restored to the active list.');
      navigate('/client/debtors', { replace: true });
      return;
    }
    logHistory(debtor.id, {
      type: DEBTOR_HISTORY_EVENT_TYPES.ARCHIVED,
      summary: 'Debtor archived',
    });
    archiveDebtors?.([debtor]);
    setNotice?.('Debtor archived.');
    navigate('/client/debtors/archived', { replace: true });
  }

  function openNewDemand() {
    if (!debtor || isArchived) return;
    logHistory(debtor.id, {
      type: DEBTOR_HISTORY_EVENT_TYPES.DEMAND_STARTED,
      summary: 'Started a new demand for this debtor',
    });
    openIntake(
      {
        ...debtor,
        name: form.Debtor_Name || debtor.name,
        company: form.Debtor_Company || debtor.company,
        country: form.Debtor_Country || debtor.country,
        street: form.Debtor_Street || debtor.street,
        city: form.Debtor_City || debtor.city,
        state: form.Debtor_State || debtor.state,
        postalCode: form.Debtor_Postal_Code || debtor.postalCode,
        email: form.Debtor_Email || debtor.email,
        accountNumber: form.External_Account_Number || debtor.accountNumber,
      },
      `/client/debtors/${encodeDebtorId(debtor.id)}`
    );
  }

  function saveProfile(e) {
    e.preventDefault();
    if (!debtor || isArchived || profileLocked) return;
    setError('');
    clearSaveToast();

    const identity = normalizeDebtorIdentity({
      contactName: form.Debtor_Name,
      company: form.Debtor_Company,
    });
    if (!identity.ok) {
      setError(debtorIdentityErrorMessage());
      return;
    }

    const postal = normalizePostalCode(form.Debtor_Postal_Code);
    const country = normalizeDebtorCountry(form.Debtor_Country, {
      postalCode: postal,
      state: form.Debtor_State,
    });
    if (!isValidPostalForCountry(postal, country)) {
      setError(postalCodeErrorMessage(postal, country));
      return;
    }

    const patch = {
      Debtor_Name: identity.Debtor_Name,
      Debtor_Company: identity.Debtor_Company,
      Debtor_Country: country,
      Debtor_Street: String(form.Debtor_Street || '').trim(),
      Debtor_City: String(form.Debtor_City || '').trim(),
      Debtor_State: String(form.Debtor_State || '').trim(),
      Debtor_Postal_Code: postal,
      Debtor_Email: String(form.Debtor_Email || '').trim(),
      External_Account_Number: String(form.External_Account_Number || '').trim(),
    };
    const changeSummary = summarizeDebtorChanges(form, patch);

    function finishContactSave(savedId) {
      const nextId = String(savedId || debtor.id);
      if (nextId && nextId !== debtor.id) {
        rememberHistory(nextId, remapDebtorHistory(debtor.id, nextId));
      }
      logHistory(nextId, {
        type: changeSummary.type,
        summary: changeSummary.summary,
      });
    }

    startTransition(async () => {
      if (isProfileOnly) {
        try {
          const saved = upsertDebtorProfile(user?.email, {
            ...patch,
            previousId: debtor.id,
          });
          if (saved.id && saved.id !== debtor.id) {
            remapArchivedDebtorId(debtor.id, saved.id);
          }
          finishContactSave(saved.id || debtor.id);
          refreshDebtors?.();
          notifySaved('Debtor record saved.');
          if (saved.id && saved.id !== debtor.id) {
            navigate(`/client/debtors/${encodeDebtorId(saved.id)}`, {
              replace: true,
              state: { from: backTo },
            });
          }
        } catch (err) {
          setError(err?.message || 'Could not save debtor record.');
        }
        return;
      }

      const editableIds = new Set(editableMatters.map((m) => String(m.ROWID)));
      let apiOk = 0;
      let apiFail = 0;

      for (const matter of editableMatters) {
        try {
          await api.put(`/server/client_api/matters/${encodeURIComponent(matter.ROWID)}`, {
            ...matter,
            ...patch,
          });
          apiOk += 1;
        } catch {
          apiFail += 1;
        }
      }

      setMatters((prev) =>
        prev.map((m) =>
          editableIds.has(String(m.ROWID)) ? { ...m, ...patch } : m
        )
      );

      const nextKey = debtorRecordKey(patch);
      try {
        upsertDebtorProfile(user?.email, {
          ...patch,
          previousId: debtor.id,
        });
        if (nextKey && nextKey !== debtor.id) {
          remapArchivedDebtorId(debtor.id, nextKey);
        }
        finishContactSave(nextKey || debtor.id);
        refreshDebtors?.();
      } catch {
        // Profile sync is best-effort alongside matter updates.
        finishContactSave(debtor.id);
      }

      if (nextKey && nextKey !== debtor.id) {
        navigate(`/client/debtors/${encodeDebtorId(nextKey)}`, {
          replace: true,
          state: { from: backTo },
        });
      }

      if (apiFail && !apiOk) {
        notifySaved('Debtor profile saved locally (API offline).');
      } else if (apiFail) {
        notifySaved(
          `Debtor profile updated on ${apiOk} demand${apiOk === 1 ? '' : 's'}; ${apiFail} could not sync.`
        );
      } else {
        notifySaved(
          `Debtor profile updated across ${editableMatters.length} open demand${
            editableMatters.length === 1 ? '' : 's'
          }.`
        );
      }
    });
  }

  if (!debtor) {
    return (
      <div className="client-page debtor-detail-page">
        <div className="section-head">
          <div>
            <h1>Debtor not found</h1>
            <p>This debtor record is unavailable or no longer matches your demands.</p>
          </div>
        </div>
      </div>
    );
  }

  const title = debtorDisplayName({
    Debtor_Name: form.Debtor_Name,
    Debtor_Company: form.Debtor_Company,
  }) || debtor.name || 'Debtor';
  const demandCount = debtor.matters.length;
  const paymentCount = paidMatters.length;
  const showSave = !profileLocked && activeTab === 'details';
  const pageClass = showSave
    ? 'client-page debtor-detail-page has-mobile-save'
    : 'client-page debtor-detail-page';

  return (
    <div className={pageClass}>
      <div className="section-head matter-detail-page-head">
        <div>
          <h1>{title}</h1>
          {form.Debtor_Company && form.Debtor_Company !== title ? (
            <p>{form.Debtor_Company}</p>
          ) : null}
        </div>
        <div className="matter-detail-page-actions debtor-detail-actions">
          <div className="debtor-detail-actions-desktop">
            <button type="button" className="btn-ghost" onClick={handleArchiveToggle}>
              {isArchived ? 'Unarchive' : 'Archive'}
            </button>
            {showSave ? (
              <div className="matter-detail-page-save">
                <button
                  type="submit"
                  form="debtor-record-form"
                  className="btn-primary"
                  disabled={pending}
                >
                  {pending ? 'Saving…' : 'Save Debtor Record'}
                </button>
              </div>
            ) : null}
          </div>

          <div className="debtor-detail-actions-mobile" ref={actionsRef}>
            <button
              type="button"
              className="debtor-detail-more-btn"
              aria-haspopup="menu"
              aria-expanded={actionsOpen}
              aria-label="More actions"
              onClick={() => setActionsOpen((open) => !open)}
            >
              <MoreActionsIcon />
            </button>
            {actionsOpen ? (
              <div className="debtor-detail-more-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="debtor-detail-more-item"
                  onClick={() => {
                    setActionsOpen(false);
                    handleArchiveToggle();
                  }}
                >
                  {isArchived ? 'Unarchive' : 'Archive'}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {isArchived ? (
        <p className="inline-notice">
          This debtor is archived. Related demands stay on Demands unless archived separately.
        </p>
      ) : null}

      <DebtorKpiStrip
        debtor={debtor}
        metricsOpen={metricsOpen}
        onToggleMetrics={setMetricsOpen}
      />

      <div className="record-tabs-wrap">
        <div
          className="record-tabs"
          role="tablist"
          aria-label="Debtor record sections"
        >
          {DEBTOR_TABS.map((tab) => {
            const selected = activeTab === tab.id;
            const hasNotify = Boolean(tabUnread[tab.id]);
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`debtor-tab-${tab.id}`}
                aria-selected={selected}
                aria-controls={`debtor-panel-${tab.id}`}
                tabIndex={selected ? 0 : -1}
                className={selected ? 'record-tab active' : 'record-tab'}
                onClick={() => setTab(tab.id)}
              >
                <span className="record-tab-label">
                  <span className="record-tab-label-full">{tab.label}</span>
                  <span className="record-tab-label-short">{tab.shortLabel}</span>
                </span>
                {hasNotify ? (
                  <span
                    className="record-tab-notify"
                    aria-label={`New activity on ${tab.label}`}
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <div className="record-tab-panels">
        {activeTab === 'details' ? (
          <section
            id="debtor-panel-details"
            role="tabpanel"
            aria-labelledby="debtor-tab-details"
            className="debtor-detail-panel"
          >
            <form id="debtor-record-form" onSubmit={saveProfile}>
              <h3>Debtor Details</h3>
              {profileLocked ? (
                <p className="inline-notice">
                  All related demands are locked after assignment. Contact details
                  can&apos;t be edited here.
                </p>
              ) : isProfileOnly ? (
                <p className="settings-sublead">
                  Contact record with no related demands yet. Use{' '}
                  <strong>New demand for this debtor</strong> on the Demands tab to open a
                  case with these details prefilled.
                </p>
              ) : (
                <p className="settings-sublead">
                  Changes only apply to unlocked demand records.
                </p>
              )}
              {error ? <p className="form-error">{error}</p> : null}

              <div className="settings-grid matter-edit-grid">
                <label className="field">
                  <span>Company</span>
                  <input
                    type="text"
                    value={form.Debtor_Company}
                    onChange={(e) => updateField('Debtor_Company', e.target.value)}
                    placeholder="Acme LLC"
                    autoComplete="organization"
                    disabled={profileLocked}
                  />
                </label>
                <label className="field">
                  <span>Contact name</span>
                  <input
                    type="text"
                    value={form.Debtor_Name}
                    onChange={(e) => updateField('Debtor_Name', e.target.value)}
                    placeholder="If known"
                    autoComplete="name"
                    disabled={profileLocked}
                  />
                </label>
                <label className="field">
                  <span className="field-label-with-tip">
                    Account #
                    <FieldTip
                      text="Your ID for this customer in your system. Demands that share this Account # consolidate into one debtor record."
                      label="Account #"
                    />
                  </span>
                  <input
                    type="text"
                    value={form.External_Account_Number}
                    onChange={(e) => updateField('External_Account_Number', e.target.value)}
                    placeholder="If known"
                    disabled={profileLocked}
                  />
                </label>
                <label className="field field-span-2">
                  <span>Street</span>
                  <AddressAutocomplete
                    value={form.Debtor_Street}
                    onChange={(next) => updateField('Debtor_Street', next)}
                    country={form.Debtor_Country}
                    disabled={profileLocked}
                    onAddressSelect={(address) => {
                      if (profileLocked) return;
                      const nextCountry = coerceDebtorCountry(address.country);
                      setForm((prev) => ({
                        ...prev,
                        Debtor_Street: address.street || prev.Debtor_Street,
                        Debtor_City: address.city || prev.Debtor_City,
                        Debtor_State: address.state || prev.Debtor_State,
                        Debtor_Postal_Code: address.postalCode || prev.Debtor_Postal_Code,
                        Debtor_Country: nextCountry || prev.Debtor_Country,
                      }));
                    }}
                    placeholder="123 Main St"
                  />
                </label>
                <label className="field">
                  <span>City</span>
                  <input
                    type="text"
                    value={form.Debtor_City}
                    onChange={(e) => updateField('Debtor_City', e.target.value)}
                    disabled={profileLocked}
                  />
                </label>
                <label className="field">
                  <span>{debtorRegionLabel(form.Debtor_Country)}</span>
                  <input
                    type="text"
                    value={form.Debtor_State}
                    onChange={(e) => {
                      const max = debtorRegionMaxLength(form.Debtor_Country);
                      const next =
                        max === 2
                          ? e.target.value.toUpperCase().slice(0, 2)
                          : e.target.value;
                      updateField('Debtor_State', next);
                    }}
                    maxLength={debtorRegionMaxLength(form.Debtor_Country)}
                    placeholder={debtorRegionPlaceholder(form.Debtor_Country)}
                    disabled={profileLocked}
                  />
                </label>
                <label className="field">
                  <span>Postal code</span>
                  <input
                    type="text"
                    value={form.Debtor_Postal_Code}
                    onChange={(e) => updateField('Debtor_Postal_Code', e.target.value)}
                    placeholder={debtorPostalPlaceholder(form.Debtor_Country)}
                    disabled={profileLocked}
                  />
                </label>
                <label className="field">
                  <span>Country</span>
                  <select
                    value={form.Debtor_Country}
                    onChange={(e) => updateField('Debtor_Country', e.target.value)}
                    disabled={profileLocked}
                  >
                    {DEBTOR_COUNTRIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field field-span-2">
                  <span>Email</span>
                  <input
                    type="email"
                    value={form.Debtor_Email}
                    onChange={(e) => updateField('Debtor_Email', e.target.value)}
                    placeholder="If known"
                    disabled={profileLocked}
                  />
                </label>
              </div>
            </form>
          </section>
        ) : null}

        {activeTab === 'demands' ? (
          <section
            id="debtor-panel-demands"
            role="tabpanel"
            aria-labelledby="debtor-tab-demands"
            className="debtor-detail-panel"
          >
            <div className="debtor-detail-panel-head">
              <div>
                <h3>
                  {demandCount} Total Demand{demandCount === 1 ? '' : 's'}
                </h3>
                <p className="settings-sublead">
                  Related demand records for this contact. Open one to review details or
                  record activity.
                </p>
              </div>
              {!isArchived ? (
                <button type="button" className="btn-primary" onClick={openNewDemand}>
                  New demand for this debtor
                </button>
              ) : null}
            </div>
            {demandCount ? (
              <ul className="debtor-related-demands">
                {debtor.matters.map((matter) => (
                  <li key={matter.ROWID}>
                    <button
                      type="button"
                      className="debtor-related-demand"
                      onClick={() =>
                        openMatter(matter, `/client/debtors/${encodeDebtorId(debtor.id)}`)
                      }
                    >
                      <span className="debtor-related-demand-top">
                        <strong>{formatMoney(matter.Amount_Owed, matter)}</strong>
                        <span className={`status-pill status-${matter.Status}`}>
                          {statusLabel(matter.Status)}
                        </span>
                      </span>
                      <span className="debtor-related-demand-meta">
                        {matterRef(matter)}
                        {' · '}
                        {formatWhen(matter.Updated_At || matter.Created_At)}
                        {isMatterRecordLocked(matter.Status) ? ' · Locked' : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="empty-inline">No related demands yet.</p>
            )}
          </section>
        ) : null}

        {activeTab === 'payments' ? (
          <section
            id="debtor-panel-payments"
            role="tabpanel"
            aria-labelledby="debtor-tab-payments"
            className="debtor-detail-panel"
          >
            <h3>Payments</h3>
            <p className="settings-sublead">
              Payments accepted from payment plans and attorney partners across this
              debtor&apos;s demands. Recovered{' '}
              {formatMattersMoneySum(paidMatters, (m) => m.Amount_Owed)}
              {paymentCount
                ? ` across ${paymentCount} paid demand${paymentCount === 1 ? '' : 's'}`
                : ''}
              {openBalanceMatters.length
                ? ` · ${formatMattersMoneySum(
                    openBalanceMatters,
                    (m) => m.Amount_Owed
                  )} still open`
                : ''}
              .
            </p>
            {paymentCount ? (
              <ul className="debtor-related-demands">
                {paidMatters.map((matter) => (
                  <li key={matter.ROWID}>
                    <button
                      type="button"
                      className="debtor-related-demand"
                      onClick={() =>
                        openMatter(matter, `/client/debtors/${encodeDebtorId(debtor.id)}`)
                      }
                    >
                      <span className="debtor-related-demand-top">
                        <strong>{formatMoney(matter.Amount_Owed, matter)}</strong>
                        <span className={`status-pill status-${matter.Status}`}>
                          {statusLabel(matter.Status)}
                        </span>
                      </span>
                      <span className="debtor-related-demand-meta">
                        {matterRef(matter)}
                        {' · Paid '}
                        {formatWhen(matter.Paid_At || matter.Updated_At || matter.Created_At)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="empty-inline">
                No plan or attorney-reported payments recorded for this debtor yet.
              </p>
            )}
          </section>
        ) : null}

        {activeTab === 'history' ? (
          <section
            id="debtor-panel-history"
            role="tabpanel"
            aria-labelledby="debtor-tab-history"
            className="debtor-detail-panel"
          >
            <h3>History</h3>
            <p className="settings-sublead">
              Activity on this debtor record — contact edits, notes, demands started from
              here, and archive changes.
            </p>
            <ol className="matter-history-timeline debtor-history-timeline">
              {history.length ? (
                [...history].reverse().map((event) => (
                  <li key={event.ROWID} className="matter-history-item">
                    <div className="matter-history-marker" aria-hidden="true" />
                    <div className="matter-history-body">
                      <div className="matter-history-meta">
                        <span className="matter-history-type">
                          {debtorHistoryTypeLabel(event.Event_Type)}
                        </span>
                        <time dateTime={event.Created_At || undefined}>
                          {formatWhen(event.Created_At)}
                        </time>
                      </div>
                      <p className="matter-history-summary">{event.Summary}</p>
                      {event.Actor_Name ? (
                        <p className="matter-history-actor">{event.Actor_Name}</p>
                      ) : null}
                    </div>
                  </li>
                ))
              ) : (
                <li className="matter-history-empty">
                  <p className="empty-inline">No history yet for this debtor.</p>
                </li>
              )}
            </ol>
          </section>
        ) : null}

        {activeTab === 'notes' ? (
          <section
            id="debtor-panel-notes"
            role="tabpanel"
            aria-labelledby="debtor-tab-notes"
            className="debtor-detail-panel"
          >
            <RecordNotesSection
              kind="debtor"
              recordId={debtor.id}
              debtor={debtor}
              user={user}
              email={user?.email}
              disabled={isArchived}
              hideTitle
              unframed
              onEntriesChange={() => {
                setNotesRevision((n) => n + 1);
                refreshDebtors?.();
              }}
              onNoteAdded={(entry) =>
                logHistory(debtor.id, {
                  type: DEBTOR_HISTORY_EVENT_TYPES.NOTE_ADDED,
                  summary: `Added note “${noteHistorySnippet(entry?.body)}”`,
                })
              }
              onNoteDeleted={(entry) =>
                logHistory(debtor.id, {
                  type: DEBTOR_HISTORY_EVENT_TYPES.NOTE_DELETED,
                  summary: `Deleted note “${noteHistorySnippet(entry?.body)}”`,
                })
              }
              lead="Private comments for your team about this debtor. Each note is saved with your name and a timestamp."
              placeholder="Add a note — relationship context, preferred contacts, collection history…"
            />
          </section>
        ) : null}
      </div>

      {showSave ? (
        <div className="debtor-detail-mobile-save">
          <button
            type="submit"
            form="debtor-record-form"
            className="btn-primary"
            disabled={pending}
          >
            {pending ? 'Saving…' : 'Save Debtor Record'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
